import { queryOptions, useQuery } from '@tanstack/react-query';

import { makeIsEnabled } from '@cast/utils';

import { useCurrentOrganizationId } from '@cast/infrastructure-organization';
import { apiClient } from '@cast/infrastructure-api';

const PERSISTENT_VOLUME_DETAILS_QUERY_KEY = 'persistent-volume-details';

type Params = {
  organizationId: string;
  clusterId?: string;
  pvName?: string;
  startTime?: string;
  endTime?: string;
  step?: string;
  enabled?: boolean;
};

const getPersistentVolumeDetailsQueryKey = ({
  organizationId,
  clusterId,
  pvName,
  startTime,
  endTime,
  step,
}: Partial<Omit<Params, 'enabled'>> = {}) =>
  [
    PERSISTENT_VOLUME_DETAILS_QUERY_KEY,
    organizationId,
    clusterId,
    pvName,
    startTime,
    endTime,
    step,
  ] as const;

const getPersistentVolumeDetailsQueryOptions = ({
  organizationId,
  clusterId,
  pvName,
  startTime,
  endTime,
  step,
  enabled = true,
}: Params) =>
  queryOptions({
    queryKey: getPersistentVolumeDetailsQueryKey({
      organizationId,
      clusterId,
      pvName,
      startTime,
      endTime,
      step,
    }),
    queryFn: async () => {
      const { data } = await apiClient.stop.getPersistentVolumeDetails({
        organizationId,
        clusterId: clusterId!,
        pvName: pvName!,
        startTime,
        endTime,
        step,
      });
      return data;
    },
    enabled: makeIsEnabled(!!organizationId, !!clusterId, !!pvName, enabled),
  });

type UsePersistentVolumeDetailsQueryArgs = Omit<Params, 'organizationId'>;

export const usePersistentVolumeDetailsQuery = (
  args: UsePersistentVolumeDetailsQueryArgs = {}
) => {
  const organizationId = useCurrentOrganizationId();

  const query = useQuery(
    getPersistentVolumeDetailsQueryOptions({ ...args, organizationId })
  );

  return { ...query, data: query.data || {} };
};
